import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TimezoneDisplayPreference, type UpdateUserSettingsInput } from '__generated__/graphql';
import { useAuthContext } from './auth.context';

export type DisplayTimezoneMode = NonNullable<UpdateUserSettingsInput['timezoneDisplayPreference']>;

interface DisplayTimezoneContextInterface {
  displayTimezoneMode: DisplayTimezoneMode;
  resolvedDisplayTimezone?: string;
  setDisplayTimezoneMode: (mode: DisplayTimezoneMode) => void;
}

const defaultTimezoneMode = TimezoneDisplayPreference.Utc;
const DISPLAY_TIMEZONE_STORAGE_KEY = 'restore.user.displayTimezonePreference';

const DisplayTimezoneContext = createContext<DisplayTimezoneContextInterface>({
  displayTimezoneMode: defaultTimezoneMode,
  resolvedDisplayTimezone: 'UTC',
  setDisplayTimezoneMode: () => {},
});

export const DisplayTimezoneContextProvider = ({ children }: { children: ReactNode }) => {
  const { user, userTenantTimezone } = useAuthContext();
  const userWithTimezonePreference = user?.user as
    | {
        id?: number | string | null;
        timezoneDisplayPreference?: DisplayTimezoneMode | null;
        timezone?: string | null;
      }
    | undefined;
  const userId = userWithTimezonePreference?.id ? String(userWithTimezonePreference.id) : undefined;
  const serverTimezoneDisplayPreference =
    userWithTimezonePreference?.timezoneDisplayPreference ?? defaultTimezoneMode;

  const readStoredTimezoneMode = (currentUserId?: string): DisplayTimezoneMode | undefined => {
    if (!currentUserId) {
      return undefined;
    }
    try {
      const stored = localStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY);
      if (!stored) {
        return undefined;
      }
      const parsed = JSON.parse(stored) as { userId?: string; mode?: DisplayTimezoneMode };
      if (parsed.userId !== currentUserId) {
        return undefined;
      }
      return parsed.mode;
    } catch {
      return undefined;
    }
  };

  const storeTimezoneMode = (currentUserId: string, mode: DisplayTimezoneMode) => {
    localStorage.setItem(
      DISPLAY_TIMEZONE_STORAGE_KEY,
      JSON.stringify({
        userId: currentUserId,
        mode,
      })
    );
  };

  const [displayTimezoneMode, setDisplayTimezoneModeState] = useState<DisplayTimezoneMode>(() => {
    const storedMode = readStoredTimezoneMode(userId);
    return storedMode ?? serverTimezoneDisplayPreference;
  });

  const setDisplayTimezoneMode = useCallback(
    (mode: DisplayTimezoneMode) => {
      setDisplayTimezoneModeState(mode);
      if (userId) {
        storeTimezoneMode(userId, mode);
      }
    },
    [userId]
  );

  useEffect(() => {
    const storedMode = readStoredTimezoneMode(userId);
    if (storedMode) {
      setDisplayTimezoneModeState(storedMode);
      return;
    }
    setDisplayTimezoneModeState(serverTimezoneDisplayPreference);
  }, [serverTimezoneDisplayPreference, userId]);

  const resolvedDisplayTimezone = useMemo(() => {
    switch (displayTimezoneMode) {
      case TimezoneDisplayPreference.Utc:
        return 'UTC';
      case TimezoneDisplayPreference.Tenant:
        return userTenantTimezone ?? 'UTC';
      case TimezoneDisplayPreference.User:
        return userWithTimezonePreference?.timezone ?? userTenantTimezone ?? 'UTC';
      case TimezoneDisplayPreference.Locale:
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
      default:
        return 'UTC';
    }
  }, [displayTimezoneMode, userTenantTimezone, userWithTimezonePreference?.timezone]);

  const contextValue = useMemo(
    () => ({ displayTimezoneMode, resolvedDisplayTimezone, setDisplayTimezoneMode }),
    [displayTimezoneMode, resolvedDisplayTimezone, setDisplayTimezoneMode]
  );

  return (
    <DisplayTimezoneContext.Provider value={contextValue}>
      {children}
    </DisplayTimezoneContext.Provider>
  );
};

export const useDisplayTimezoneContext = () => useContext(DisplayTimezoneContext);
